import name, { createDuplicateForeignContactsAliasNameValidator } from 'forms/shared/validators/models/name/name';
import cityCountry from 'forms/shared/validators/models/location/models/cityCountry';
import locationAddressModelValidator from 'forms/shared/validators/models/location/models/address';
import physicalAddress from 'forms/shared/validators/models/physicalAddress/physicalAddress';
import { OTHER, DEFAULT_LATEST } from 'constants/dateLimits';
import { sortSafeDateTimes, getSafeDateTime } from 'helpers/date';
import { ValidateJSModel } from 'forms/shared/types/validatejs';
import { DateBaseValidatorOptions } from 'forms/shared/validators/base/date/types';
import { LAST_CONTACT_DATE_TOO_EARLY, REQUIRED } from 'constants/errors';
import { setToStartOfMonth } from 'helpers/date';
import { isPVQForm } from 'helpers/form';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';
import { foreignContactMethodOptions, foreignContactRelationshipOptions, foreignNationalWorkOptions } from 'constants/enums/foreignActivityOptions';
import pvqNameModelValidator from 'forms/shared/validators/models/pvqName/name';
import phoneModelValidator from 'forms/shared/validators/models/phone';
import emailModelValidator from 'forms/shared/validators/models/pvqEmail';
import { hasYesOrNo } from 'models/validate';
import { isSafeUnitedStates } from 'helpers/locations';
import { countryString } from '../../../../../validators/location';
import { ForeignContactsItemModel } from '../types';
import { createPvqAliasesValidator } from './pvqAliasesValidator';

const contactMethodOptions = ['In person', 'Telephone', 'Electronic', 'Written', 'Other'];

const frequencyOptions = ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Annually', 'Other'];

const relationshipOptions = ['Professional', 'Personal', 'Obligation', 'Other'];

export const foreignContactsItemValidator: ValidateJSModel<ForeignContactsItemModel> = {
  Name: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        model: { validator: pvqNameModelValidator },
      };
    }

    if (attributes?.NameNotApplicable?.applicable === false) { return {}; }

    return {
      presence: true,
      model: { validator: name },
    };
  },

  NameExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    if (attributes?.NameNotApplicable?.applicable === true) { return {}; }

    return {
      hasValue: true,
    };
  },

  FirstContact: (value, attributes, attributeName, options) => {
    const { Birthdate } = attributes;
    const { applicantBirthdate } = options;

    const sortedDates = sortSafeDateTimes([
      { ...getSafeDateTime(Birthdate), field: 'Birthdate' },
      { ...getSafeDateTime(applicantBirthdate), field: 'applicantBirthdate' },
    ]);

    const earliestDate = sortedDates.length ? setToStartOfMonth(sortedDates[sortedDates.length - 1]) : null;

    return {
      presence: true,
      date: {
        earliest: earliestDate?.datetime,
        latest: DEFAULT_LATEST,
        tooEarly: earliestDate?.field === 'Birthdate' ? 'error.date.sponsoredForeignNationalDateTooEarly' : 'error.date.min'
      },
    };
  },

  LastContact: (value, attributes) => {
    const dateLimits: DateBaseValidatorOptions = { latest: DEFAULT_LATEST };

    if (attributes?.FirstContact) {
      dateLimits.earliest = attributes?.FirstContact;
      dateLimits.tooEarly = LAST_CONTACT_DATE_TOO_EARLY;
    }

    return {
      presence: true,
      date: dateLimits,
    };
  },

  Methods: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        array: {
          length: { minimum: 1, message: REQUIRED },
          validator: { inclusion: foreignContactMethodOptions },
        }
      };
    }

    return {
      presence: true,
      array: {
        length: { minimum: 1, message: REQUIRED },
        validator: { inclusion: contactMethodOptions },
      }
    };
  },

  MethodsExplanation: (value, attributes) => {
    if (attributes?.Methods?.values?.includes('Other')) {
      return { hasValue: true };
    }

    return {};
  },

  Frequency: {
    hasValue: {
      validator: {
        inclusion: frequencyOptions,
      },
    },
  },

  FrequencyExplanation: (value, attributes) => {
    if (attributes?.Frequency?.value === 'Other') {
      return { hasValue: true };
    }

    return {};
  },

  Relationship: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    return {
      presence: true,
      array: {
        length: { minimum: 1, message: REQUIRED },
        validator: { inclusion: relationshipOptions },
      }
    };
  },

  RelationshipPvq: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        hasValue: {
          validator: { inclusion: foreignContactRelationshipOptions },
        },
      };
    }

    return {};
  },

  RelationshipExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (attributes?.RelationshipPvq?.value === 'Other') {
        return { hasValue: true };
      }

      return {};
    }

    if (
      attributes?.Relationship?.values?.includes('Other')
      || attributes?.Relationship?.values?.includes('Obligation')
    ) {
      return { hasValue: true };
    }

    return {};
  },

  Aliases: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        branchCollection: {
          validator: createPvqAliasesValidator(value, attributes),
        },
      };
    }

    return {
      presence: true,
      branchCollection: {
        validator: {
          Alias: {
            model: { validator: createDuplicateForeignContactsAliasNameValidator(value, attributes) },
          },
        },
      },
    };
  },

  Citizenship: { hasValue: true, country: true },

  Birthdate: (value, attributes) => {
    if (attributes?.BirthdateNotApplicable?.applicable === false) { return {}; }

    return {
      presence: true,
      date: OTHER,
    };
  },

  Birthplace: (value, attributes, attributeName, options) => {
    if (attributes?.BirthplaceNotApplicable?.applicable === false) { return {}; }
    if (isPVQForm(options?.formType)) {
      return {
        location: {
          requireIsMilitaryDiplomaticAddress: false,
          requireStreet: false,
          requireCity: true,
          requireCountry: true,
          requireState: false,
          requireZipCode: false,
          validator: pvqLocationModelValidator,
        },
        presence: true,
      };
    }
    return {
      presence: true,
      location: {
        validator: cityCountry,
      },
    };
  },

  BirthplaceExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    if (attributes?.BirthplaceNotApplicable?.applicable !== false) { return {}; }

    return {
      hasValue: true,
    };
  },

  Address: (value, attributes, attributeName, options) => {
    if (attributes?.AddressNotApplicable?.applicable === false) { return {}; }
    if (attributes?.InUsaNotApplicable?.applicable === false) {
      return {};
    }

    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        location: {
          validator: pvqLocationModelValidator,
          requireStreet: true,
          requireIsMilitaryDiplomaticAddress: true,
          requireMilitaryDiplomaticInstallationName: true,
          requireApoFpoDpoPostcode: !isSafeUnitedStates(value),
        },
      };
    }

    return {
      presence: true,
      location: {
        validator: locationAddressModelValidator,
      },
    };
  },

  AlternateAddress: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    if (countryString(attributes?.Address?.country) !== 'United States'
      && attributes?.AddressNotApplicable?.applicable !== false) {
      return {
        presence: true,
        model: {
          validator: physicalAddress,
          militaryAddress: true,
          hasTelephone: false,
          country: attributes?.Address?.country,
        },
      };
    }

    return {};
  },

  Employer: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && (attributes?.NeverWorked?.checked === true)) {
      return {};
    }

    if (attributes?.EmployerNotApplicable?.applicable === false) { return {}; }

    return {
      hasValue: true,
    };
  },

  EmployerAddress: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (attributes?.EmployerNotApplicable?.applicable === false
        || attributes?.NeverWorked?.checked === true
        || attributes?.EmployerAddressNotApplicable?.applicable === false) {
        return {};
      }

      return {
        presence: true,
        location: {
          validator: pvqLocationModelValidator,
          requireStreet: true,
          requireIsMilitaryDiplomaticAddress: true,
          requireMilitaryDiplomaticInstallationName: true,
          requireApoFpoDpoPostcode: !isSafeUnitedStates(value),
        },
      };
    }

    if (attributes?.EmployerAddressNotApplicable?.applicable === false) { return {}; }

    return {
      presence: true,
      location: {
        validator: locationAddressModelValidator,
      },
    };
  },

  HasAffiliations: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    return {
      hasValue: {
        validator: {
          inclusion: ['Yes', 'No', "I don't know"],
        },
      }
    };
  },

  Affiliations: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    if (attributes?.HasAffiliations?.value === 'Yes') {
      return {
        hasValue: true,
      };
    }

    return {};
  },

  NameNotApplicable: {},

  BirthdateNotApplicable: {},

  BirthplaceNotApplicable: {},

  AddressNotApplicable: {},

  InUsaNotApplicable: {},

  EmployerAddressNotApplicable: {},

  EmployerNotApplicable: {},

  HowCitizenshipAcquired: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.HowCitizenshipAcquiredNotApplicable?.applicable === true) {
      return { presence: true, hasValue: true };
    }

    return {};
  },

  HowCitizenshipAcquiredNotApplicable: {},

  Telephone: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        model: {
          validator: phoneModelValidator,
          requireNumberType: true
        },
      };
    }

    return {};
  },

  EmailNotApplicable: {},

  Email: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.EmailNotApplicable?.applicable === true) {
      return {
        model: {
          validator: emailModelValidator,
          requireEmailType: false
        }
      };
    }
    return {};
  },

  OccupationNotApplicable: {},

  Occupation: (value, attributes, attributeName, options) => {
    if (
      isPVQForm(options?.formType)
      && attributes?.OccupationNotApplicable?.applicable !== false
      && attributes?.NeverWorked?.checked !== true
      && attributes?.EmployerNotApplicable?.applicable !== false
    ) {
      return { presence: true, hasValue: true };
    }

    return {};
  },

  ForeignDisciplinesNotApplicable: {},

  ForeignDisciplines: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.ForeignDisciplinesNotApplicable?.applicable === true) {
      return {
        presence: true,
        array: {
          length: { minimum: 1, message: REQUIRED },
          validator: { inclusion: foreignNationalWorkOptions },
        }
      };
    }

    return {};
  },

  ForeignDisciplinesOtherExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.ForeignDisciplines?.values?.includes('Other Government Agency')) {
      return { hasValue: true };
    }

    return {};
  },

  ForeignAssociationDescriptionNotApplicable: {},

  ForeignAssociationDescription: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && attributes?.ForeignAssociationDescriptionNotApplicable?.applicable !== false
      && (
        attributes?.ForeignDisciplines?.values?.includes('Military or Armed Forces')
        || attributes?.ForeignDisciplines?.values?.includes('Security Service')
        || attributes?.ForeignDisciplines?.values?.includes('Intelligence Service')
        || attributes?.ForeignDisciplines?.values?.includes('Defense Industry')
        || attributes?.ForeignDisciplines?.values?.includes('Foreign Movement (such as a social, political, or religious movement)')
      )
    ) {
      return { hasValue: true };
    }

    return {};
  },

  HasObligations: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        presence: true, hasValue: { validator: hasYesOrNo }
      };
    }

    return {};
  },

  Obligations: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.HasObligations?.value === 'Yes') {
      return { hasValue: true };
    }

    return {};
  },
};
